import { MenuOptions } from "@qivia/ui/src/designSystem/components/Menu/MenuOptions";
import {
  AddOutlined,
  ButtonDS,
  SideBarCompanies,
  TextCapitalized,
} from "@qivia/ui";
import { Radio } from "@qivia/ui/src/designSystem/components/Radio";
import { Spacer } from "@qivia/ui/src/designSystem/components/Spacer";
import styled from "styled-components";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import { useTranslation } from "react-i18next";
import { useOutsideClick } from "@qivia/ui/src/hooks/useOutsideClick";
import { useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../redux/hooks";
import { selectManager } from "../routes/home/homeSlice";
import {
  loadAllCompaniesAsync,
  selectAllCompaniesStatus,
} from "../routes/home/companies/companiesSlice";
import type { CompaniesList } from "../routes/home/companies/companiesApi";
import "../i18n/i18n";

type CompanySelectorProps = {
  canCreateCompany: boolean;
  companies: CompaniesList[];
  onClickCompanyCreation: () => void;
  currentCompany: string;
  onChangeCompany: (companyUuid: string) => void;
  size: "L" | "S";
};

export const CompanySelector = (props: CompanySelectorProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [isMenuOptionsCompaniesOpen, setIsMenuOptionsCompaniesOpen] =
    useState<boolean>(false);
  const [listDisplayed, setListDisplayed] = useState<CompaniesList[]>([]);

  const outsideCompaniesMenuOptions = useOutsideClick(() => {
    setIsMenuOptionsCompaniesOpen(false);
  });

  const manager = useAppSelector(selectManager);
  const allCompaniesStatus = useAppSelector(selectAllCompaniesStatus);

  useEffect(() => {
    if (manager && allCompaniesStatus === "idle") {
      void dispatch(loadAllCompaniesAsync());
    }
  }, [allCompaniesStatus, dispatch, manager]);

  const activeCompanies = useMemo(
    () =>
      props.companies.filter((company) => company.providerDeletedAt === null),
    [props.companies],
  );

  useEffect(() => {
    setListDisplayed(activeCompanies);
  }, [activeCompanies]);

  const searchBarProps = {
    values: activeCompanies,
    setFilterValues: setListDisplayed,
    keysToIgnore: ["uuid" as const],
  };

  return (
    <StyledMenu ref={outsideCompaniesMenuOptions}>
      <StyledMenuOptionCompanies>
        {isMenuOptionsCompaniesOpen && (
          <>
            {props.size === "L" ? <Spacer x={2} /> : <Spacer x={11} />}
            <StyledColumn>
              <MenuOptions
                hasHoverBackground
                options={listDisplayed.map((company) => {
                  return {
                    action: () => {
                      props.onChangeCompany(company.uuid);
                    },
                    label: (
                      <StyledWordBreak>
                        <StyledJustify>
                          <StyledColumn>
                            <StyledCompaniesTitle>
                              <TextCapitalized>
                                {t(`menuOption.entities`)}
                              </TextCapitalized>
                            </StyledCompaniesTitle>
                            <TextCapitalized>{company.name}</TextCapitalized>
                          </StyledColumn>
                          <Radio
                            isSelected={props.currentCompany === company.name}
                          />
                        </StyledJustify>
                        <Spacer x={0.25} />
                      </StyledWordBreak>
                    ),
                    icon: (
                      <SideBarCompanies
                        text={company.name}
                        version={"S"}
                        onClick={() => {
                          props.onChangeCompany(company.uuid);
                        }}
                        hasFocusBorder={true}
                      />
                    ),
                  };
                })}
                bottomButton={
                  props.canCreateCompany ? (
                    <ButtonDS
                      format={"fill"}
                      buttonType={"secondary"}
                      text={t(`menuOption.entities.add`)}
                      leftIcon={<AddOutlined />}
                      onClick={props.onClickCompanyCreation}
                    />
                  ) : undefined
                }
                searchBar={searchBarProps}
                width="20"
              />
            </StyledColumn>
          </>
        )}
      </StyledMenuOptionCompanies>
      <SideBarCompanies
        text={props.currentCompany}
        version={props.size}
        onClick={() =>
          setIsMenuOptionsCompaniesOpen(!isMenuOptionsCompaniesOpen)
        }
        hasFocusBorder={isMenuOptionsCompaniesOpen}
      />
    </StyledMenu>
  );
};

const StyledWordBreak = styled.div`
  display: flex;
  word-break: break-all;
`;

const StyledJustify = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
`;

const StyledColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledCompaniesTitle = styled.div`
  display: flex;
  flex-direction: column;
  color: ${colors["colors/text/lightGrey"]};
  ${typographies["Body/XS"]};
`;
const StyledMenu = styled.div`
  width: 100%;
`;

const StyledMenuOptionCompanies = styled.div`
  z-index: 500;
  position: absolute;
  top: 6rem;
`;
